import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type PortalMember = {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  role: string;
  createdAt: string;
  lastSeenAt: string | null;
};

export function useUsers() {
  const { currentTenantId } = useTenant();

  const { data, isLoading } = useQuery({
    queryKey: ["portal-users", currentTenantId],
    queryFn: () => portalFetch<PortalMember[]>(`/api/portal/tenants/${currentTenantId}/users`),
    enabled: !!currentTenantId,
  });

  return { users: data ?? [], isLoading };
}

export function useCreateUser() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { email: string; name?: string; role?: "viewer" | "admin" }) =>
      portalFetch<PortalMember>(`/api/portal/tenants/${currentTenantId}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, ...input }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-users"] }),
  });
}

export type BulkImportResult = {
  created: number;
  skipped: number;
  errors: { email: string; error: string }[];
};

export function useBulkImportUsers() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (users: { email: string; name?: string; role?: "viewer" | "admin" }[]) =>
      portalFetch<BulkImportResult>(`/api/portal/tenants/${currentTenantId}/users/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, users }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-users"] }),
  });
}

export function useDeleteUser() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/users/${userId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, userId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-users"] }),
  });
}

export function useUpdateUserRole() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { userId: string; role: "viewer" | "admin" }) =>
      portalFetch<PortalMember>(`/api/portal/tenants/${currentTenantId}/users/${input.userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, ...input }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-users"] }),
  });
}
